import type { DragHandle } from './drag';

/**
 * The shape a placement ghost is drawn from — the Anfahrt / Arbeit / Abfahrt split of wherever a
 * drag or a planning draft would land. Pure types + formatting, no React.
 *
 * Building a layout from an existing entry stays with the board (`buildPreviewLayout`), because
 * that needs the entry's domain shape; drawing one does not, which is why the type lives here.
 */

export interface PreviewSegment {
  kind: 'travel' | 'work' | 'departure';
  minutes: number;
  color: string;
  /** `45 min`, or the new `HH:mm` on the work strip of a whole-block move. */
  label: string;
  /** The segment this drag is changing — drawn at full colour while the rest dim. */
  active: boolean;
}

export interface PreviewLayout {
  /** Minutes from the window start. May be negative, or past `BOARD_TOTAL_MINUTES`. */
  startMin: number;
  endMin: number;
  segments: PreviewSegment[];
}

/**
 * Which strip a handle changes. `move` changes all of them, so nothing dims.
 *
 * Exported because `ScheduleBlock` answers the same question one step earlier — hovering a resize
 * strip highlights the segment it is about to change.
 */
export const ACTIVE_SEGMENT_KIND: Record<DragHandle, PreviewSegment['kind'] | null> = {
  move: null,
  travel: 'travel',
  work: 'work',
  departure: 'departure',
  // The absence handles resize the one bar an absence has.
  start: 'work',
  end: 'work',
};

/**
 * `45 min`, `4 h`, `2:15 h`. A whole-day absence as `480 min` is technically right and unreadable.
 */
export function formatDuration(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  if (total < 60) return `${total} min`;

  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h} h` : `${h}:${String(m).padStart(2, '0')} h`;
}
